import { useEffect, useMemo, useState } from 'react';
import {
  getCompetitorsAdCreativesExtInfo,
  ITaggingInfo,
} from '@opm/services/competitorsAdCreatives';
import { useStableValue } from '@opm/utils/stableKey';

/**
 * 批量获取素材扩展信息（视频标签 + 内容摘要）
 *
 * 在列表数据返回后，提取 video_url 批量请求 ext_info 接口，
 * 返回 extInfoMap 以 video_url 为 key 的映射。
 */
export function useExtInfo({
  records,
  gameIds,
}: {
  records: any[];
  gameIds: string[];
}) {
  const [extInfoMap, setExtInfoMap] = useState<Record<string, ITaggingInfo>>(
    {},
  );
  const [loading, setLoading] = useState(false);

  // 从记录中提取有效的 video_url 列表
  const videoUrls = useMemo(() => {
    if (!records || records.length === 0) return [];
    const urls: { video_url: string; game_id: string }[] = [];
    for (const r of records) {
      const url = r.video_url;
      if (url)
        urls.push({
          video_url: url,
          game_id: r.game_id,
        });
    }
    return urls;
  }, [records]);

  // 深比较稳定引用，避免 records 引用变化但 video_url 未变时重复请求
  const stableVideoUrls = useStableValue(videoUrls);
  const stableGameIds = useStableValue(gameIds);

  useEffect(() => {
    if (
      stableVideoUrls.length === 0 ||
      !stableGameIds ||
      stableGameIds.length === 0
    ) {
      return;
    }

    setLoading(true);
    getCompetitorsAdCreativesExtInfo({
      data: { videos: stableVideoUrls },
    })
      .then(res => {
        const map: Record<string, ITaggingInfo> = {};
        (res?.tagging_info || []).forEach(item => {
          if (item.video_url) {
            map[item.video_url] = item;
          }
        });
        setExtInfoMap(map);
      })
      .catch(() => {
        // 请求失败保持旧数据
      })
      .finally(() => {
        setLoading(false);
      });
  }, [stableVideoUrls, stableGameIds]);

  return { extInfoMap, extInfoLoading: loading };
}
